import { apiSlice } from '../apiSlice';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	BlindBoxDetailDTO,
	BlindBoxDTO,
	BlindBoxGameDTO,
	BlindBoxSeriesDTO,
	BoxFilter,
	BoxSingleDto,
	SelectTreasureFilter,
} from '../DTO/exchange/box.interface';

export const boxApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getBoxList: builder.query<
			SuccessResponse<Paginated<BlindBoxDTO>>,
			BoxFilter
		>({
			query: (body) => ({
				url: `/web/blind-box/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['boxList'],
		}),
		getSelectTreasureList: builder.query<
			SuccessResponse<Paginated<BlindBoxSeriesDTO>>,
			SelectTreasureFilter
		>({
			query: (body) => ({
				url: `/web/blind-box/game/page`,
				method: 'POST',
				body,
			}),
		}),
		getBoxSortList: builder.query<SuccessResponse<BlindBoxDTO[]>, null>({
			query: () => ({
				url: `/web/blind-box/sort`,
				method: 'GET',
			}),
		}),
		sortBox: builder.mutation<SuccessResponse<any>, string[]>({
			query: (body) => ({
				url: `/web/blind-box/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['boxList'],
		}),
		createBox: builder.mutation<SuccessResponse<any>, BlindBoxDetailDTO>({
			query: (body) => ({
				url: `/web/blind-box`,
				method: 'POST',
				body,
			}),
		}),
		updateBox: builder.mutation<SuccessResponse<any>, BlindBoxDetailDTO>({
			query: (body) => ({
				url: `/web/blind-box/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['boxDetail'],
		}),
		getBoxDetail: builder.query<SuccessResponse<BlindBoxDetailDTO>, string>({
			query: (id) => ({
				url: `/web/blind-box/${id}`,
				method: 'GET',
			}),
			providesTags: ['boxDetail'],
		}),
		deleteBox: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/blind-box/${id}`,
				method: 'DELETE',
			}),
		}),
		getBoxTreasureList: builder.query<
			SuccessResponse<Paginated<BlindBoxGameDTO>>,
			BoxFilter
		>({
			query: (body) => ({
				url: `/web/blind-box/${body.id}/game/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['boxTreasureList'],
		}),
		updateBoxTreasureList: builder.mutation<
			SuccessResponse<any>,
			{ id: string; arr: string[] }
		>({
			query: (body) => ({
				url: `/web/blind-box/${body.id}/game`,
				method: 'PUT',
				body: body.arr,
			}),
			invalidatesTags: ['boxTreasureList'],
		}),
		getBoxTreasureSortList: builder.query<
			SuccessResponse<BlindBoxGameDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/blind-box/${id}/game/sort`,
				method: 'GET',
			}),
		}),
		getBoxTreasureSelectedList: builder.query<
			SuccessResponse<string[]>,
			string
		>({
			query: (id) => ({
				url: `/web/blind-box/${id}/game/selected`,
				method: 'GET',
			}),
		}),
		sortBoxTreasureList: builder.mutation<
			SuccessResponse<any>,
			{ id: string; arr: string[] }
		>({
			query: (body) => ({
				url: `/web/blind-box/${body.id}/game/sort`,
				method: 'PUT',
				body: body.arr,
			}),
			invalidatesTags: ['boxTreasureList'],
		}),
		getBoxSingleList: builder.query<
			SuccessResponse<Paginated<BoxSingleDto>>,
			BoxFilter
		>({
			query: (body) => ({
				url: `/web/blind-box/task/cumulative/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['boxSingleList'],
		}),
		createBoxSingle: builder.mutation<SuccessResponse<any>, BoxSingleDto>({
			query: (body) => ({
				url: `/web/blind-box/task/cumulative`,
				method: 'POST',
				body: body,
			}),
		}),
		updateBoxSingle: builder.mutation<SuccessResponse<any>, BoxSingleDto>({
			query: (body) => ({
				url: `/web/blind-box/task/cumulative/${body.id}`,
				method: 'PUT',
				body: body,
			}),
			invalidatesTags: ['boxSingleDetail'],
		}),
		setBoxSingleStatus: builder.mutation<
			SuccessResponse<any>,
			{ blindBoxId: string; publishList: { id: string; published: number }[] }
		>({
			query: (body) => ({
				url: `/web/blind-box/task/cumulative/published`,
				method: 'PUT',
				body: body,
			}),
			invalidatesTags: ['boxSingleList', 'boxSingleDetail'],
		}),
		getBoxSingleDetail: builder.query<SuccessResponse<BoxSingleDto>, string>({
			query: (id) => ({
				url: `/web/blind-box/task/cumulative/${id}`,
				method: 'GET',
			}),
			providesTags: ['boxSingleDetail'],
		}),
		deleteBoxSingle: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/blind-box/task/cumulative/${id}`,
				method: 'DELETE',
			}),
		}),
	}),
});

export const {
	useGetBoxListQuery,
	useGetBoxSortListQuery,
	useSortBoxMutation,
	useCreateBoxMutation,
	useUpdateBoxMutation,
	useGetBoxDetailQuery,
	useGetSelectTreasureListQuery,
	useDeleteBoxMutation,
	useGetBoxSingleListQuery,
	useGetBoxTreasureListQuery,
	useUpdateBoxTreasureListMutation,
	useGetBoxTreasureSortListQuery,
	useSortBoxTreasureListMutation,
	useCreateBoxSingleMutation,
	useUpdateBoxSingleMutation,
	useSetBoxSingleStatusMutation,
	useGetBoxSingleDetailQuery,
	useDeleteBoxSingleMutation,
	useGetBoxTreasureSelectedListQuery,
} = boxApiSlice;
